import React from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Grid, Skeleton, Typography } from '@mui/material';
import { GroupsOutlined, LocationOn, StarRounded } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import SectionContainer from '../../components/SectionContainer';
import RevealOnScroll from '../../components/RevealOnScroll';

const API_BASE =
  (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

function getImageUrl(gallery) {
  if (!gallery || gallery.length === 0) return null;
  const raw = gallery[0];
  if (raw.startsWith('/uploads/')) {
    return `${API_BASE}${raw}`;
  }
  return raw;
}

function StarRow({ rating }) {
  if (!rating || rating === 0) {
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
        Newly listed
      </Typography>
    );
  }
  const filled = Math.round(rating);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <StarRounded
          key={i}
          sx={{ fontSize: 16, color: i < filled ? '#F7B32B' : 'rgba(0,0,0,0.15)' }}
          aria-hidden="true"
        />
      ))}
      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
        (rated)
      </Typography>
    </Box>
  );
}

StarRow.propTypes = {
  rating: PropTypes.number,
};

StarRow.defaultProps = {
  rating: 0,
};

function InstituteCard({ institute }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;

  const imageUrl = getImageUrl(institute.gallery);
  const cardBg = isDark ? 'rgba(27,54,93,0.5)' : theme.palette.background.paper;
  const cardBorder = isDark
    ? '1px solid rgba(255,255,255,0.07)'
    : '1px solid rgba(27,54,93,0.06)';

  const courses = Array.isArray(institute.courses_offered)
    ? institute.courses_offered.slice(0, 3)
    : [];

  const showSuccessChip =
    institute.success_rate != null && Number(institute.success_rate) > 0;
  const showEstChip =
    !showSuccessChip && institute.established_year != null;

  return (
    <Box
      sx={{
        borderRadius: '20px',
        background: cardBg,
        border: cardBorder,
        boxShadow: isDark
          ? '0 4px 20px rgba(0,0,0,0.2)'
          : '0 4px 20px rgba(27,54,93,0.06)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: isDark
            ? '0 20px 40px -8px rgba(0,0,0,0.45), 0 0 0 1px rgba(242,101,34,0.2)'
            : '0 20px 40px -16px rgba(242,101,34,0.25), 0 0 0 1px rgba(242,101,34,0.12)',
        },
      }}
    >
      {/* Hero image */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingTop: '62.5%',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={`${institute.name} institute`}
            loading="lazy"
            decoding="async"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: `linear-gradient(135deg, ${theme.palette.brand.navy.dark}, ${theme.palette.brand.navy.main})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: 'rgba(255,255,255,0.15)',
                fontWeight: 800,
                letterSpacing: '-0.02em',
                userSelect: 'none',
              }}
            >
              BML
            </Typography>
          </Box>
        )}

        {/* Overlay chip */}
        {showSuccessChip && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              px: 1.25,
              py: 0.4,
              borderRadius: '999px',
              background: `linear-gradient(135deg, ${orange}, ${golden})`,
              color: '#fff',
              fontSize: '0.65rem',
              fontWeight: 800,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              boxShadow: '0 2px 8px rgba(242,101,34,0.4)',
            }}
            aria-label={`${institute.success_rate}% success rate`}
          >
            {institute.success_rate}% Success
          </Box>
        )}
        {!showSuccessChip && showEstChip && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              px: 1.25,
              py: 0.4,
              borderRadius: '999px',
              background: 'rgba(0,0,0,0.45)',
              color: 'rgba(255,255,255,0.85)',
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
            }}
          >
            Est. {institute.established_year}
          </Box>
        )}
      </Box>

      {/* Card body */}
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.25, flexGrow: 1 }}>
        {/* Name */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '0.975rem',
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {institute.name}
        </Typography>

        {/* City */}
        {institute.city && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {institute.city}
            </Typography>
          </Box>
        )}

        {/* Star rating */}
        <StarRow rating={Number(institute.rating) || 0} />

        {/* Course chips */}
        {courses.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {courses.map((course) => (
              <Chip
                key={course}
                label={course}
                size="small"
                sx={{
                  fontSize: '0.68rem',
                  height: 22,
                  bgcolor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(27,54,93,0.06)',
                  color: 'text.secondary',
                  border: 'none',
                }}
              />
            ))}
          </Box>
        )}

        {/* Students enrolled */}
        {institute.total_students > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 'auto', pt: 0.5 }}>
            <GroupsOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {Number(institute.total_students).toLocaleString('en-IN')}+ students enrolled
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

InstituteCard.propTypes = {
  institute: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    city: PropTypes.string,
    gallery: PropTypes.arrayOf(PropTypes.string),
    courses_offered: PropTypes.arrayOf(PropTypes.string),
    rating: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_students: PropTypes.number,
    success_rate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    established_year: PropTypes.number,
  }).isRequired,
};

function InstituteCardSkeleton() {
  return (
    <Box
      sx={{
        borderRadius: '20px',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Skeleton variant="rectangular" sx={{ width: '100%', paddingTop: '62.5%', height: 0 }} />
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Skeleton variant="text" width="70%" height={24} />
        <Skeleton variant="text" width="45%" height={16} />
        <Skeleton variant="text" width="55%" height={16} />
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          <Skeleton variant="rounded" width={60} height={22} />
          <Skeleton variant="rounded" width={70} height={22} />
          <Skeleton variant="rounded" width={55} height={22} />
        </Box>
      </Box>
    </Box>
  );
}

function FeaturedInstitutesSection({ institutes, loading }) {
  if (!loading && (!institutes || institutes.length === 0)) {
    return null;
  }

  return (
    <SectionContainer
      eyebrow="Real institutes"
      title="Institutes already growing with BookMyLibrary."
      subtitle="Here's a glimpse of the institutes that trust us with their leads, their profiles, and their next batch of students."
    >
      <Grid container spacing={3}>
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={`skeleton-${i}`}>
                <InstituteCardSkeleton />
              </Grid>
            ))
          : institutes.map((institute, index) => (
              <Grid item xs={12} sm={6} md={4} key={institute.id || index}>
                <RevealOnScroll delay={Math.min(index * 0.07, 0.5)}>
                  <InstituteCard institute={institute} />
                </RevealOnScroll>
              </Grid>
            ))}
      </Grid>
    </SectionContainer>
  );
}

FeaturedInstitutesSection.propTypes = {
  institutes: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default FeaturedInstitutesSection;
